import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { catalog } from '@/api';
import type { City } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './CityPicker.module.css';

/**
 * F-170 B3.4 — city dropdown that persists the selection in
 * `localStorage`.
 *
 * Source of truth flow:
 *   `localStorage` → `<CityPicker value>` (controlled or self-controlled)
 *   user picks → `onChange(slug | null)` + `localStorage.setItem`
 *
 * `null` means "Усі міста" — callers omit the `?city=` query param.
 * Cities load from the existing `/cities/` REST endpoint once and are
 * memoised in component state; the parent doesn't have to pre-fetch.
 */
export const CITY_STORAGE_KEY = 'olabotardivna.city_slug';

export function readStoredCitySlug(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CITY_STORAGE_KEY);
    return raw && raw !== 'null' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredCitySlug(slug: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (slug) window.localStorage.setItem(CITY_STORAGE_KEY, slug);
    else window.localStorage.removeItem(CITY_STORAGE_KEY);
  } catch {
    // localStorage may throw in private mode; swallow silently.
  }
}

export interface CityPickerProps {
  /** Controlled value (slug) or `null` for "Усі міста". When undefined
   *  the picker reads/writes `localStorage` itself. */
  value?: string | null;
  onChange?: (slug: string | null) => void;
  /** Override the label of the "all cities" sentinel option. */
  allLabel?: string;
  className?: string;
}

export function CityPicker({
  value,
  onChange,
  allLabel = 'Усі міста',
  className,
}: CityPickerProps): JSX.Element {
  const [cities, setCities] = useState<City[]>([]);
  // Self-controlled fallback so the atom can live anywhere without a
  // boilerplate `useState + useEffect` in every host.
  const [internal, setInternal] = useState<string | null>(() => readStoredCitySlug());
  const current = value !== undefined ? value : internal;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await catalog.fetchCities();
        if (!cancelled) setCities(data.results ?? []);
      } catch (err) {
        logger.warn('CityPicker: cities fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (next: string | null): void => {
    if (value === undefined) setInternal(next);
    writeStoredCitySlug(next);
    onChange?.(next);
  };

  return (
    <label className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <Icon name="map-pin" size={14} className={styles.icon} aria-label="Місто" />
      <select
        className={styles.select}
        value={current ?? ''}
        onChange={(e) => handleChange(e.target.value || null)}
        aria-label="Місто"
      >
        <option value="">{allLabel}</option>
        {cities.map((c) => (
          <option key={c.slug} value={c.slug}>
            {c.name_ua}
          </option>
        ))}
      </select>
      <span className={styles.caret} aria-hidden>
        ▾
      </span>
    </label>
  );
}
